/**
 * fix-79: an approval that runs after the click.
 *
 * Royce (19 Oct): "approve feels slow (10–15 s before the card clears on signup and draft approvals)". The desk now
 * answers Approve with 202 once the card is claimed, and the run (pre-flight, the portal writes and their read-backs,
 * Resend, Slack) continues on the desk. The console moves on to the next card at once and follows each run here by
 * polling GET /api/cards/:id/progress until it ends. Pure, so every transition is tested (test/approving.test.ts).
 *
 * fix-84 (card 1621, 19 Oct): the run is the desk-runs queue's, not the request's (Cloudflare cancelled 1621's T31 30 s
 * after the 202). A run that shows no progress says exactly what to do: when it last moved, not to approve again, when
 * the drain will fail it, and to check the portal and Retry after that.
 */
import type { PortalWait } from './api';
import { parseUtc } from './time';

/** What GET /api/cards/:id/progress answers: one cards row, no views. */
export interface Progress {
  id: number;
  status: string;
  claimed_at: string | null;
  claim_age_s: number | null;
  /** When the drain's 15-minute rule fails an unfinished claim (fix-84). */
  stale_at?: string | null;
  exec: { step: string | null; steps?: [string, number][]; responded_ms?: number | null; total_ms?: number;
    /** Epoch ms of the run's last save: its last sign of progress (fix-84). */
    updated?: number } | null;
  error_text: string | null;
  portal_wait: PortalWait | null;
}

export type RunPhase = 'running' | 'executed' | 'failed' | 'expired' | 'waiting' | 'continues' | 'lost';

export interface Run {
  id: number;
  type: string;
  who: string;
  phase: RunPhase;
  step: string | null;
  reason: string | null;
  started: number;
  ended: number | null;
  errors: number;
  totalMs: number | null;
  /** Epoch ms of the run's last progress (the desk's clock), and when the drain fails it, from the last poll (fix-84). */
  updated: number | null;
  staleAt: number | null;
  /** When this console last saw the run move (its own clock): a new step or save since the poll before. */
  movedAt: number;
  /** No movement for STALLED_MS: still followed (every 15 s) and said so; it ends only as the card does, or at the
   * drain's mark. */
  slow: boolean;
}

/** The drain's rule (cards.js DEAD_WORKER_MINUTES): a claim this old on an unfinished card is a run that died. */
export const STALE_CLAIM_S = 15 * 60;
/** Polls that fail in a row before the console stops following. */
export const MAX_POLL_ERRORS = 5;
/** An ending that needs nothing from him leaves the tray after this; the rest stay until opened or closed. */
export const CLEAR_MS = 8_000;

export function startRun(card: { id: number; type: string }, who: string, now: number): Run {
  return { id: card.id, type: card.type, who, phase: 'running', step: null, reason: null, started: now, ended: null, errors: 0, totalMs: null, updated: null, staleAt: null, movedAt: now, slow: false };
}

const end = (run: Run, phase: RunPhase, now: number, reason: string | null = null, totalMs: number | null = run.totalMs): Run =>
  ({ ...run, phase, reason, step: null, ended: now, totalMs });

// The error text's first part: the desk writes "<reason>\n\nWhat to do: <next>".
const firstPart = (s: string | null) => (s ?? '').split('\n\nWhat to do:')[0].trim() || null;

/** One poll's answer. */
export function onProgress(run: Run, p: Progress, now: number): Run {
  if (run.phase !== 'running') return run;
  const totalMs = p.exec?.total_ms ?? null;
  const ok = { ...run, errors: 0 };
  if (p.status === 'executed') return end(ok, 'executed', now, null, totalMs);
  if (p.status === 'failed') return end(ok, 'failed', now, firstPart(p.error_text) ?? 'it failed — open the card', totalMs);
  if (p.status === 'expired') return end(ok, 'expired', now, firstPart(p.error_text) ?? 'pre-flight refused it — open the card', totalMs);
  if (p.status === 'approved' && !p.claimed_at) {
    return p.portal_wait
      ? end(ok, 'waiting', now, p.portal_wait.reason, totalMs)
      // A part done (a broadcast's chunk): the drain continues it every ten minutes.
      : end(ok, 'continues', now, 'continues on the drain every ten minutes', totalMs);
  }
  if (p.status === 'approved') {
    const updated = p.exec?.updated ?? ok.updated;
    const step = p.exec?.step ?? null;
    const moved = updated !== ok.updated || step !== ok.step;
    const seen = { ...ok, step, updated, staleAt: parseUtc(p.stale_at) ?? ok.staleAt, movedAt: moved ? now : ok.movedAt,
      slow: moved ? false : ok.slow };
    if ((p.claim_age_s ?? 0) >= STALE_CLAIM_S) {
      return end(seen, 'lost', now, `The run stopped at ${stepLabel(seen.step) ?? 'an unknown step'}. The drain marks it failed at `
        + `its next tick and lists what was done; then check the account in the portal and press Retry.`);
    }
    return seen;
  }
  return end(ok, 'expired', now, `it is now ${p.status}`, totalMs);
}

/** A poll that failed (the desk unreachable, a 5xx). */
export function onPollError(run: Run, message: string, now: number): Run {
  if (run.phase !== 'running') return run;
  const errors = run.errors + 1;
  return errors >= MAX_POLL_ERRORS
    ? end({ ...run, errors }, 'lost', now, `the desk stopped answering (${message}) — open the card to see where it got to`)
    : { ...run, errors };
}

/** A run that has not moved for this long is slow: said so, and still followed (fix-84 addendum: card 1621's run took
 * 2 min 10 s with a 52 s pre-flight; the tray gave up on it and never showed it done). */
export const STALLED_MS = 60_000;
/** How often a slow run is asked about. */
export const SLOW_POLL_MS = 15_000;

/** "13:19", Brisbane — the operator's clock. */
const clock = (ms: number) => new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Brisbane', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(ms);

/** Before each poll: a run that has not moved for STALLED_MS is marked slow — never ended here. It ends as the card
 * does (executed, failed…), or as lost once its claim reaches the drain's 15 minutes (onProgress). */
export function onTick(run: Run, now: number): Run {
  if (run.phase !== 'running' || run.slow || now - run.movedAt < STALLED_MS) return run;
  return { ...run, slow: true };
}

// The slow line: when it last moved, at which step, not to approve again, still checking, and what happens at the mark.
function slowLine(run: Run): string {
  const at = stepLabel(run.step);
  const since = run.updated ?? run.started;
  const mark = run.staleAt ? `If it has not finished by ${clock(run.staleAt)}, the drain marks it failed`
    : 'If it has not finished 15 minutes after the approval, the drain marks it failed';
  return `Slow — no progress since ${clock(since)}${at ? ` (${at})` : ''}. Don't approve it again; still checking. `
    + `${mark} and lists what was done; then check the account in the portal and press Retry.`;
}

/** Milliseconds until the next poll, or null when the run has ended. */
export function pollDelay(run: Run, now: number): number | null {
  if (run.phase !== 'running') return null;
  if (run.slow) return SLOW_POLL_MS;
  return now - run.started < 20_000 ? 1_000 : 3_000;
}

/** Whether the tray drops this run now. */
export function cleared(run: Run, now: number): boolean {
  return ['executed', 'continues', 'waiting'].includes(run.phase) && run.ended !== null && now - run.ended >= CLEAR_MS;
}

export const needsYou = (run: Run) => ['failed', 'expired', 'lost'].includes(run.phase);

const SENDS = new Set(['email_draft', 'setup_rescue', 'send_template', 'service_notice_broadcast']);
const verb = (type: string) => (type === 'approve_signup' ? 'Activating' : SENDS.has(type) ? 'Sending' : 'Working');
const doneWord = (type: string) => (type === 'approve_signup' ? 'Activated' : SENDS.has(type) ? 'Sent' : 'Done');

const STEPS: Record<string, string> = {
  queued: 'waiting for the desk to start it',
  'pre-flight': 'pre-flight checks',
  'Get-Token': 'a portal token',
  'Get-Token (new)': 'a new portal token',
  'Get-Customer-Details': 'reading the account',
  'Get-Account-Status': 'reading the IOU limit',
  'Get-Account-Balance': 'reading the balance',
  'Activate-Account': 'Activate-Account',
  'Update-IOU-Limit': 'Update-IOU-Limit',
  'Add-One-Off-Transaction': 'adding the trial credit',
  'Suspend-Account': 'Suspend-Account',
  'Reactivate-Account': 'Reactivate-Account',
  'Cancel-Account': 'Cancel-Account',
  'wait for the balance': 'waiting for the balance to show the credit',
  'wait before a second Activate-Account': 'waiting to try Activate-Account again',
  'closed-account check': 'checking the account is open',
  Resend: 'handing it to Resend',
  'Resend wire id': 'recording the message id',
  'outcome baseline': 'recording the baseline',
  'Slack redraw': 'updating Slack',
};

/** The innermost step, in words ("pre-flight › Get-Customer-Details" is "reading the account"). */
export function stepLabel(step: string | null): string | null {
  if (!step) return null;
  const inner = step.split(' › ').at(-1) as string;
  return STEPS[inner] ?? inner;
}

/** The run's one line, for the tray and the card. */
export function runLine(run: Run): string {
  const secs = run.totalMs !== null ? ` in ${(run.totalMs / 1000).toFixed(1)} s` : '';
  switch (run.phase) {
    case 'running': return run.slow ? slowLine(run) : `${verb(run.type)}… ${stepLabel(run.step) ?? 'starting'}`;
    case 'executed': return `${doneWord(run.type)}${secs}`;
    case 'failed': return `Failed: ${run.reason}`;
    case 'expired': return `Not done: ${run.reason}`;
    case 'waiting': return `Waiting for the portal: ${run.reason} — it tries every 10 minutes`;
    case 'continues': return `Part done: ${run.reason}`;
    case 'lost': return run.reason ?? 'no longer followed';
  }
}
